import React from 'react';
import cn from 'classnames';
import { Icon } from 'admin/components/common/Icon';
import PanelTooltip from 'admin/components/pages/Story/PanelTooltip';
import t from 'utils/translate';
import FillPreview from '../FillPreview';
import css from './TokenPill.scss';

// Token names are short paths like "Size / General D1"; wrapping them reads as two labels
export const NOWRAP_TOOLTIP = { maxWidth: 'none' };

/** Pill tooltip: the token or asset name, then its current value in a muted tone */
export function TokenTooltip({ name, value }: { name: string; value?: string }) {
	return (
		<span className={css.tooltipLabel}>
			{name} {value ? <span className={css.tooltipValue}>{value}</span> : null}
		</span>
	);
}

/** The id in `var(--sc-token-<id>)`, which still names a token that no longer exists */
export const getTokenReferenceId = (reference: string): string | undefined =>
	reference.match(/--sc-token-([a-z0-9-]+)/i)?.[1];

type Props = {
	/** `row` (Background list rows): regular weight, roomier padding, names not truncated early */
	variant?: 'field' | 'row';
	/** Accessible name of the pill button, e.g. `Choose a token: brand / Navy` */
	label: string;
	/** Text after the swatch; omit for a swatch-only pill */
	value?: React.ReactNode;
	/** A color token's current value, shown as a swatch */
	swatch?: string;
	/** A semantic asset's current image, as a CSS `background-image` value */
	image?: string;
	tooltip?: React.ReactNode;
	unresolved?: boolean;
	open?: boolean;
	disabled?: boolean;
	/** Opens the token or asset picker; without it the pill is display-only and its row handles clicks */
	onClick?: () => void;
	onUnlink?: () => void;
	unlinkLabel?: string;
	/** Placement in the host layout, e.g. margins that let the pill hang past a value column */
	className?: string;
};

/** The one linked-token or linked-asset pill: swatch or thumbnail, value, and an unlink button revealed on hover. */
export default function TokenPill({
	variant = 'field',
	label,
	value,
	swatch,
	image,
	tooltip,
	unresolved,
	open,
	disabled,
	onClick,
	onUnlink,
	unlinkLabel = t('designTokens.unlink'),
	className,
}: Props) {
	const content = (
		<>
			{swatch !== undefined ? <FillPreview value={swatch} className={css.swatch} /> : null}
			{image !== undefined ? <span className={css.thumbnail} style={{ backgroundImage: image }} /> : null}
			{value !== undefined ? <span className={css.value}>{value}</span> : null}
		</>
	);
	const body = onClick ? (
		<button
			type="button"
			className={css.button}
			aria-label={label}
			aria-expanded={open}
			disabled={disabled}
			onClick={event => {
				event.stopPropagation();
				onClick();
			}}
		>
			{content}
		</button>
	) : (
		<span className={css.button} aria-label={label}>
			{content}
		</span>
	);

	return (
		<span
			className={cn(css.pill, className)}
			data-variant={variant}
			data-unresolved={!!unresolved}
			data-open={!!open}
		>
			{tooltip ? (
				<PanelTooltip content={tooltip} open={open ? false : undefined} stylesContainer={NOWRAP_TOOLTIP}>
					{body}
				</PanelTooltip>
			) : (
				body
			)}
			{onUnlink ? (
				<PanelTooltip content={unlinkLabel}>
					<button
						type="button"
						className={css.unlink}
						aria-label={unlinkLabel}
						disabled={disabled}
						onClick={event => {
							event.stopPropagation();
							onUnlink();
						}}
					>
						<Icon type="sc-unbind" width={14} />
					</button>
				</PanelTooltip>
			) : null}
		</span>
	);
}
